import { useCallback, useEffect, useState } from 'react';
import './App.css';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const API_KEY = process.env.REACT_APP_MAIL_AI_API_KEY || '';

async function api(path, options = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
      ...options.headers,
    },
    ...options,
  });
  const body = await response.json().catch(() => ({}));

  if (!response.ok || body.success === false) {
    const error = body.error;
    throw new Error(
      typeof error === 'string' ? error : JSON.stringify(error || 'Request failed')
    );
  }

  return body.data;
}

function formatDate(value) {
  if (!value) return '—';

  const normalized = typeof value === 'string' && value.includes(' ')
    ? `${value.replace(' ', 'T')}Z`
    : value;
  const date = new Date(normalized);

  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
}

function statusClass(status) {
  if (status === 'success') return 'badge badge-success';
  if (status === 'failed' || status === 'rejected') return 'badge badge-error';
  return 'badge';
}

function App() {
  const [tab, setTab] = useState('rules');
  const [rules, setRules] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [logs, setLogs] = useState([]);
  const [newInstruction, setNewInstruction] = useState('');
  const [loadingRules, setLoadingRules] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [busy, setBusy] = useState({});
  const [results, setResults] = useState({});
  const [notice, setNotice] = useState(null);

  const loadRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      const data = await api('/api/rules');
      setRules(data);
      setDrafts(Object.fromEntries(data.map((rule) => [rule.id, rule.instruction])));
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setLoadingRules(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      setLogs(await api('/api/logs?limit=100'));
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  useEffect(() => {
    if (tab === 'logs') loadLogs();
  }, [tab, loadLogs]);

  function setRuleBusy(id, action) {
    setBusy((current) => ({ ...current, [id]: action }));
  }

  function replaceRule(updated) {
    setRules((current) => current.map((rule) => (
      rule.id === updated.id ? updated : rule
    )));
    setDrafts((current) => ({ ...current, [updated.id]: updated.instruction }));
  }

  async function saveRule(rule, quiet = false) {
    const instruction = (drafts[rule.id] || '').trim();
    const updated = await api(`/api/rules/${rule.id}`, {
      method: 'PUT',
      body: JSON.stringify({ instruction, enabled: Boolean(rule.enabled) }),
    });

    replaceRule(updated);
    setResults((current) => {
      const next = { ...current };
      delete next[rule.id];
      return next;
    });
    if (!quiet) setNotice({ type: 'success', text: `Rule #${rule.id} saved.` });
    return updated;
  }

  async function handleSave(rule) {
    setRuleBusy(rule.id, 'Saving');
    setNotice(null);
    try {
      await saveRule(rule);
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setRuleBusy(rule.id, null);
    }
  }

  async function handleCompile(rule) {
    setRuleBusy(rule.id, 'Compiling');
    setNotice(null);
    try {
      let currentRule = rule;
      if ((drafts[rule.id] || '').trim() !== rule.instruction) {
        currentRule = await saveRule(rule, true);
      }
      const compiledRule = await api(`/api/rules/${currentRule.id}/compile`, {
        method: 'POST',
      });
      replaceRule(compiledRule);
      setResults((current) => ({
        ...current,
        [rule.id]: {
          title: 'Compiled rule',
          content: compiledRule.compiled_json,
        },
      }));
      setNotice({ type: 'success', text: `Rule #${rule.id} compiled.` });
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
      await loadRules();
    } finally {
      setRuleBusy(rule.id, null);
    }
  }

  async function handlePreview(rule) {
    setRuleBusy(rule.id, 'Previewing');
    setNotice(null);
    try {
      const data = await api(`/api/rules/${rule.id}/dry-run`, { method: 'POST' });
      setResults((current) => ({
        ...current,
        [rule.id]: {
          title: 'Preview',
          content: data,
        },
      }));
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setRuleBusy(rule.id, null);
    }
  }

  async function handleExecute(rule) {
    if (!window.confirm(`Execute rule #${rule.id} against Gmail now?`)) return;

    setRuleBusy(rule.id, 'Executing');
    setNotice(null);
    try {
      const data = await api(`/api/rules/${rule.id}/execute`, { method: 'POST' });
      setResults((current) => ({
        ...current,
        [rule.id]: {
          title: 'Execution result',
          content: data,
        },
      }));
      setNotice({
        type: 'success',
        text: `Rule #${rule.id} executed. ${data.affected_count} message(s) affected.`,
      });
      await loadRules();
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setRuleBusy(rule.id, null);
    }
  }

  async function handleToggle(rule) {
    setRuleBusy(rule.id, 'Saving');
    setNotice(null);
    try {
      const updated = await api(`/api/rules/${rule.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          instruction: drafts[rule.id],
          enabled: !Boolean(rule.enabled),
        }),
      });
      replaceRule(updated);
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setRuleBusy(rule.id, null);
    }
  }

  async function handleDelete(rule) {
    if (!window.confirm(`Delete rule #${rule.id}? Its logs will also be deleted.`)) return;

    setRuleBusy(rule.id, 'Deleting');
    setNotice(null);
    try {
      await api(`/api/rules/${rule.id}`, { method: 'DELETE' });
      setRules((current) => current.filter((item) => item.id !== rule.id));
      setNotice({ type: 'success', text: `Rule #${rule.id} deleted.` });
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setRuleBusy(rule.id, null);
    }
  }

  async function handleCreate(event) {
    event.preventDefault();
    const instruction = newInstruction.trim();
    if (instruction.length < 5) return;

    setRuleBusy('new', 'Adding');
    setNotice(null);
    try {
      const created = await api('/api/rules', {
        method: 'POST',
        body: JSON.stringify({ instruction, enabled: true }),
      });
      setRules((current) => [created, ...current]);
      setDrafts((current) => ({ ...current, [created.id]: created.instruction }));
      setNewInstruction('');
      setNotice({ type: 'success', text: `Rule #${created.id} added.` });
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setRuleBusy('new', null);
    }
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">MAIL AI</p>
          <h1>Rule manager</h1>
        </div>
        <nav aria-label="Main navigation">
          <button
            className={tab === 'rules' ? 'nav-button active' : 'nav-button'}
            onClick={() => setTab('rules')}
          >
            Rules <span>{rules.length}</span>
          </button>
          <button
            className={tab === 'logs' ? 'nav-button active' : 'nav-button'}
            onClick={() => setTab('logs')}
          >
            Action log
          </button>
          <a
            className="nav-button"
            href="https://mail-ai.ejerome.net/api/gmail/oauth/url"
            target="_blank"
            rel="noopener noreferrer"
            title="Open Google consent to renew Gmail access (opens in a new tab)"
          >
            Reconnect Gmail
          </a>
        </nav>
      </header>

      <main>
        {notice && (
          <div className={`notice ${notice.type}`} role="status">
            <span>{notice.text}</span>
            <button onClick={() => setNotice(null)} aria-label="Dismiss notification">×</button>
          </div>
        )}

        {tab === 'rules' ? (
          <>
            <section className="panel create-panel">
              <div>
                <h2>Add an instruction</h2>
                <p>Describe which messages to find and what Mail AI should do.</p>
              </div>
              <form onSubmit={handleCreate}>
                <textarea
                  value={newInstruction}
                  onChange={(event) => setNewInstruction(event.target.value)}
                  placeholder="Move LinkedIn and Indeed emails from my inbox to the Jobs label"
                  rows="3"
                  minLength="5"
                  required
                />
                <button
                  className="button primary"
                  type="submit"
                  disabled={busy.new || newInstruction.trim().length < 5}
                >
                  {busy.new || 'Add rule'}
                </button>
              </form>
            </section>

            <div className="section-heading">
              <div>
                <h2>Rules</h2>
                <p>Edit, compile, preview, then execute.</p>
              </div>
              <button className="button subtle" onClick={loadRules} disabled={loadingRules}>
                {loadingRules ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            {loadingRules && rules.length === 0 ? (
              <div className="empty-state">Loading rules…</div>
            ) : rules.length === 0 ? (
              <div className="empty-state">No rules yet. Add your first instruction above.</div>
            ) : (
              <div className="rule-list">
                {rules.map((rule) => {
                  const dirty = (drafts[rule.id] || '') !== rule.instruction;
                  const isBusy = Boolean(busy[rule.id]);
                  const compiled = Boolean(rule.compiled_at) && !dirty;

                  return (
                    <article className="panel rule-card" key={rule.id}>
                      <div className="rule-header">
                        <div className="rule-title">
                          <span className="rule-id">#{rule.id}</span>
                          <span className={compiled ? 'badge badge-success' : 'badge'}>
                            {rule.compile_error ? 'Compile error' : compiled ? 'Compiled' : 'Not compiled'}
                          </span>
                          <span className={rule.enabled ? 'badge badge-enabled' : 'badge'}>
                            {rule.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <label className="switch-label">
                          <input
                            type="checkbox"
                            checked={Boolean(rule.enabled)}
                            onChange={() => handleToggle(rule)}
                            disabled={isBusy}
                          />
                          Enabled
                        </label>
                      </div>

                      <textarea
                        className="rule-instruction"
                        value={drafts[rule.id] || ''}
                        onChange={(event) => setDrafts((current) => ({
                          ...current,
                          [rule.id]: event.target.value,
                        }))}
                        rows="3"
                        aria-label={`Instruction for rule ${rule.id}`}
                      />

                      {rule.compile_error && (
                        <div className="inline-error">{rule.compile_error}</div>
                      )}

                      <div className="rule-meta">
                        <span>Compiled: {formatDate(rule.compiled_at)}</span>
                        <span>Last run: {formatDate(rule.last_run_at)}</span>
                      </div>

                      <div className="rule-actions">
                        <button
                          className="button"
                          onClick={() => handleSave(rule)}
                          disabled={isBusy || !dirty || (drafts[rule.id] || '').trim().length < 5}
                        >
                          Save
                        </button>
                        <button
                          className="button primary"
                          onClick={() => handleCompile(rule)}
                          disabled={isBusy || (drafts[rule.id] || '').trim().length < 5}
                        >
                          Compile
                        </button>
                        <button
                          className="button"
                          onClick={() => handlePreview(rule)}
                          disabled={isBusy || !compiled}
                        >
                          Preview
                        </button>
                        <button
                          className="button danger"
                          onClick={() => handleExecute(rule)}
                          disabled={isBusy || !compiled}
                        >
                          Execute
                        </button>
                        <button
                          className="button text-danger"
                          onClick={() => handleDelete(rule)}
                          disabled={isBusy}
                        >
                          Delete
                        </button>
                        {isBusy && <span className="busy-label">{busy[rule.id]}…</span>}
                      </div>

                      {results[rule.id] && (
                        <details className="result" open>
                          <summary>{results[rule.id].title}</summary>
                          <pre>{JSON.stringify(results[rule.id].content, null, 2)}</pre>
                        </details>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="section-heading">
              <div>
                <h2>Action log</h2>
                <p>The 100 most recent rule executions.</p>
              </div>
              <button className="button subtle" onClick={loadLogs} disabled={loadingLogs}>
                {loadingLogs ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            <section className="panel log-panel">
              {loadingLogs && logs.length === 0 ? (
                <div className="empty-state">Loading action log…</div>
              ) : logs.length === 0 ? (
                <div className="empty-state">No actions have been logged yet.</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Rule</th>
                        <th>Action</th>
                        <th>Status</th>
                        <th>Affected</th>
                        <th>Query / error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id}>
                          <td className="nowrap">{formatDate(log.date_created)}</td>
                          <td>
                            <strong>#{log.rule_id}</strong>
                            <span className="table-secondary">{log.instruction || 'Deleted rule'}</span>
                          </td>
                          <td>
                            {log.action_type}
                            {log.target_label && (
                              <span className="table-secondary">→ {log.target_label}</span>
                            )}
                          </td>
                          <td><span className={statusClass(log.status)}>{log.status}</span></td>
                          <td>{log.affected_count}</td>
                          <td className={log.error ? 'log-error' : 'log-query'}>
                            {log.error || log.gmail_query || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

export default App;
